import DataSourceConfig from '../Config/DataSourceConfig';
import SystemDataService from '../Services/SystemDataService';
import { EventEntity } from '../Entities';

// Gallery/cover images may be authored either as a full URL or as an
// assets-data key (upload photos to assets-data.js, then reference the key on
// the event), EventEntity resolves keys to URLs during normalization.
// NOTE: keep everything above the first `export` free of Entity references, // scripts/seed-content-to-s3.js evaluates that part in a sandbox to extract
// LOCAL_EVENTS.

/**
 * EVENTS DATA (local mock)
 * ──────────────────────────────────────────────────────────────────────────
 * Events are the core feature of the site. For now they live here as a static
 * mock so we can design against them. The plan is:
 *
 *     this file  →  S3 (system_data_{lang}.json → content.eventsData)  →  Zoho
 *
 * The event schema below is intentionally "full", it carries everything a
 * best-practice event page needs (dates + times, location, gallery, SEO,
 * agenda, speakers, registration) so nothing has to be re-modelled when the
 * data moves to S3 / Zoho. Optional fields can simply be omitted per event.
 *
 * ── Event shape ────────────────────────────────────────────────────────────
 *  id            unique id (stable across sources)
 *  status        false → hidden everywhere
 *  featured      bool, pinned / highlighted
 *  slug          url segment → /events/:slug
 *  language      'he' | 'en'  (one object per language, like blogs)
 *  category      free-text tag shown as a pill
 *
 *  title, subtitle
 *  seo           { title, description, keywords[], image }
 *
 *  startDate     'YYYY-MM-DD'  (local calendar date, no timezone math)
 *  endDate       'YYYY-MM-DD'  (== startDate for single-day; later for multi-day)
 *  startTime     'HH:mm' | null
 *  endTime       'HH:mm' | null
 *  allDay        bool
 *  timezone      IANA tz string (informational)
 *
 *  location      { name, address, city, region, country, mapUrl, online, url }
 *  image         cover / hero image
 *  thumbnail     optional square used in the calendar pill (falls back to image)
 *  gallery       [{ url, alt }]
 *
 *  intro         short lead paragraph
 *  description   long body (supports the same inline-markdown as blogs)
 *  sections      [{ heading, content, bullets[], image }]  (reuses blog shape)
 *  agenda        [{ time, title, description }]             (run-of-show)
 *  highlights    ["what to expect" bullets]
 *  speakers      [{ name, role, image }]
 *  faqList       [{ question, answer }]
 *  tags          [string]
 *
 *  price         number (0 = free)
 *  currency      ISO 4217, e.g. 'USD'
 *  registrationClosed  bool, true → show "Registration closed" instead of the ticket CTA
 *  registration  { url, label, capacity, spotsLeft, deadline }
 *  organizer     { name, email, phone }
 *
 *  publishedAt, updatedAt   ISO datetimes
 *  source        'local' | 's3' | 'zoho'   (provenance hint)
 *  externalId    id in the source system (Zoho record id later)
 */

// Per-event payment page. For the mock every event points at the same page;
// when data moves to S3/Zoho this becomes a real per-event link.
const PAYMENT_URL = 'https://www.example.com';

// Event covers reference keys in assets-data.js (resolved to URLs by resolveImg).
const IMG = {
    bourbon:   '/placeholder.svg',
    axe:       '/placeholder.svg',
    beach:     '/placeholder.svg',
    speaker:   '/placeholder.svg',
};

const LOCAL_EVENTS = [
    // ── 1. Event One ──────────
    {
        id: 'event-axe-throwing-0709',
        status: true,
        featured: false,
        slug: 'event-one',
        language: 'en',
        category: 'Social',
        title: 'Event One',
        subtitle: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
        seo: {
            title: 'Event One | Your Business',
            description: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
            keywords: ['keyword one', 'keyword two', 'keyword three', 'keyword four', 'keyword five'],
            image: IMG.axe,
        },
        startDate: '2026-07-09',
        endDate: '2026-07-09',
        startTime: '19:30',
        endTime: null,
        allDay: false,
        timezone: 'America/New_York',
        location: {
            name: 'Venue Name',
            address: null,
            city: null,
            region: 'Region',
            country: 'USA',
            mapUrl: null,
            online: false,
            url: null,
        },
        image: IMG.axe,
        gallery: [{ url: IMG.axe, alt: 'Add your content here.' }, { url: '/placeholder.svg', alt: 'Add your content here.' }],
        intro: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
        description: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
        sections: [],
        agenda: [],
        highlights: ['Add your content here.', 'Add your content here.', 'Add your content here.'],
        faqList: [{ question: 'Frequently asked question?', answer: 'Answer goes here.' }],
        tags: ['social', 'night out'],
        price: 30,
        currency: 'USD',
        registrationClosed: true,
        registration: { url: '/contact', paymentUrl: PAYMENT_URL, label: 'Registration closed', capacity: null, spotsLeft: 0, deadline: '2026-07-09' },
        organizer: { name: 'Your Business', email: 'hello@example.com', phone: null },
        publishedAt: '2026-06-20T09:00:00.000Z',
        updatedAt: '2026-07-09T09:00:00.000Z',
        source: 'local',
        externalId: null,
    },

    // ── 2. Event Two ─
    {
        id: 'event-bourbon-rabbi-0715',
        status: true,
        featured: false,
        slug: 'event-two',
        language: 'en',
        category: 'Tasting',
        title: 'Event Two',
        subtitle: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
        seo: {
            title: 'Event Two | Your Business',
            description: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
            keywords: ['keyword one', 'keyword two', 'keyword three', 'keyword four', 'keyword five'],
            image: IMG.bourbon,
        },
        startDate: '2026-07-15',
        endDate: '2026-07-15',
        startTime: '19:00',
        endTime: null,
        allDay: false,
        timezone: 'America/New_York',
        location: {
            name: '123 Example St',
            address: '123 Example St',
            city: 'City Name',
            region: 'Region',
            country: 'USA',
            mapUrl: 'https://www.example.com',
            online: false,
            url: null,
        },
        image: IMG.bourbon,
        gallery: [{ url: IMG.bourbon, alt: 'Add your content here.' }],
        intro: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
        description: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
        sections: [],
        agenda: [],
        highlights: ['Add your content here.', 'Add your content here.', 'Add your content here.'],
        faqList: [],
        tags: ['bourbon', 'tasting'],
        price: 0,
        currency: 'USD',
        registrationClosed: true,
        registration: { url: '/contact', paymentUrl: PAYMENT_URL, label: 'Registration closed', capacity: null, spotsLeft: 0, deadline: '2026-07-15' },
        organizer: { name: 'Your Business', email: 'hello@example.com', phone: null },
        publishedAt: '2026-06-25T09:00:00.000Z',
        updatedAt: '2026-07-15T09:00:00.000Z',
        source: 'local',
        externalId: null,
    },

    // ── 3. Event Three ─────────────────────────────
    {
        id: 'event-tu-bav-white-0729',
        status: true,
        featured: true,
        slug: 'event-three',
        language: 'en',
        category: 'Party',
        title: 'Event Three',
        subtitle: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
        seo: {
            title: 'Event Three | Your Business',
            description: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
            keywords: ['keyword one', 'keyword two', 'keyword three', 'keyword four', 'keyword five'],
            image: IMG.beach,
        },
        startDate: '2026-07-29',
        endDate: '2026-07-29',
        startTime: '19:30',
        endTime: null,
        allDay: false,
        timezone: 'America/New_York',
        location: {
            name: 'Venue Name',
            address: null,
            city: null,
            region: 'Region',
            country: 'USA',
            mapUrl: null,
            online: false,
            url: null,
        },
        image: IMG.beach,
        gallery: [{ url: IMG.beach, alt: 'Add your content here.' }],
        intro: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
        description: 'Add your content here. This placeholder describes the section and can be replaced with your own copy.',
        sections: [],
        agenda: [],
        highlights: ['Add your content here.', 'Add your content here.', 'Add your content here.', 'Add your content here.'],
        speakers: [{ name: 'Speaker Name', role: 'Speaker Role', image: IMG.speaker }],
        faqList: [{ question: 'Frequently asked question?', answer: 'Answer goes here.' }],
        tags: ['tag one', 'tag two', 'tag three'],
        price: 0,
        currency: 'USD',
        registration: { url: '/contact', paymentUrl: PAYMENT_URL, label: 'RSVP', capacity: null, spotsLeft: null, deadline: '2026-07-29' },
        organizer: { name: 'Your Business', email: 'hello@example.com', phone: null },
        publishedAt: '2026-07-01T09:00:00.000Z',
        updatedAt: '2026-07-20T09:00:00.000Z',
        source: 'local',
        externalId: null,
    },
];

/**
 * Fetch all events. In 'local' mode returns the mock above (optionally filtered
 * by language). In 'backend' mode fetches from S3-backed content, falling back
 * to local on any error, same contract as getBlogsData().
 * Normalization / active-filter / chronological sort live on EventEntity.
 */
export const getEventsData = async (language) => {
    const localEvents = EventEntity.list(LOCAL_EVENTS, { language });

    if (DataSourceConfig.events === 'local') return localEvents;

    try {
        const data = await SystemDataService.getEventsData();

        return Array.isArray(data) && data.length > 0
            ? EventEntity.list(data, { language })
            : localEvents;
    } catch (_) {
        return localEvents;
    }
};

export const getEventBySlug = async (slug, language) => {
    const pickLocal = () => {
        const matches = EventEntity.list(LOCAL_EVENTS).filter((e) => e.slug === slug);
        return matches.find((e) => !language || e.language === language) || matches[0] || null;
    };

    if (DataSourceConfig.events === 'local') return pickLocal();

    try {
        const data = await SystemDataService.getEventBySlug(slug);

        if (data && !Array.isArray(data)) {
            return EventEntity.isActiveRaw(data) ? EventEntity.from(data) : pickLocal();
        }

        if (Array.isArray(data) && data.length > 0) {
            return EventEntity.list(data)[0] || pickLocal();
        }

        return pickLocal();
    } catch (_) {
        return pickLocal();
    }
};

export default LOCAL_EVENTS;
